import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { createEmptySlice, LEGACY_MENU_ID, parseStoredCartState, persistCartState, } from '@/context/cart/cartStorage';
import { addComboToSlice, addProductToSlice, setComboQuantityInSlice, setProductQuantityInSlice, } from '@/context/cart/cartLines';
const CartContext = createContext(null);
export function CartProvider({ children }) {
    const initial = useMemo(() => parseStoredCartState(), []);
    const [activeMenuId, setActiveMenuId] = useState(initial.activeMenuId);
    const [cartsByMenuId, setCartsByMenuId] = useState(initial.cartsByMenuId);
    const [unavailableIds, setUnavailableIds] = useState([]);
    const checkoutMenuId = activeMenuId === LEGACY_MENU_ID ? null : activeMenuId;
    const activeSlice = activeMenuId != null ? (cartsByMenuId[activeMenuId] ?? createEmptySlice()) : createEmptySlice();
    const updateActiveSlice = useCallback((updater) => {
        if (activeMenuId == null)
            return;
        setCartsByMenuId((prev) => ({
            ...prev,
            [activeMenuId]: updater(prev[activeMenuId] ?? createEmptySlice()),
        }));
    }, [activeMenuId]);
    const setCheckoutMenuId = useCallback((action) => {
        setActiveMenuId((prevActive) => {
            const prevPublic = prevActive === LEGACY_MENU_ID ? null : prevActive;
            const resolved = typeof action === 'function' ? action(prevPublic) : action;
            if (resolved == null)
                return null;
            setCartsByMenuId((prev) => {
                const legacySlice = prev[LEGACY_MENU_ID];
                if ((prevActive === LEGACY_MENU_ID || prevPublic == null) && legacySlice && !prev[resolved]) {
                    const next = { ...prev, [resolved]: legacySlice };
                    delete next[LEGACY_MENU_ID];
                    return next;
                }
                if (prev[resolved])
                    return prev;
                return { ...prev, [resolved]: createEmptySlice() };
            });
            return resolved;
        });
    }, []);
    const setDeliveryType = useCallback((nextType) => updateActiveSlice((slice) => ({ ...slice, deliveryType: nextType })), [updateActiveSlice]);
    const setDeliveryAddress = useCallback((nextAddress) => updateActiveSlice((slice) => ({ ...slice, deliveryAddress: nextAddress })), [updateActiveSlice]);
    const setPickupRestaurantId = useCallback((action) => {
        updateActiveSlice((slice) => {
            const nextValue = typeof action === 'function' ? action(slice.pickupRestaurantId) : action;
            return { ...slice, pickupRestaurantId: nextValue };
        });
    }, [updateActiveSlice]);
    useEffect(() => {
        persistCartState(activeMenuId, cartsByMenuId);
    }, [activeMenuId, cartsByMenuId]);
    const addToCart = useCallback((product, quantity = 1, selectedModifiers = []) => {
        updateActiveSlice((slice) => addProductToSlice(slice, product, quantity, selectedModifiers));
    }, [updateActiveSlice]);
    const addComboToCart = useCallback((combo, quantity = 1) => {
        updateActiveSlice((slice) => addComboToSlice(slice, combo, quantity));
    }, [updateActiveSlice]);
    const removeProductFromCart = useCallback((lineId) => {
        updateActiveSlice((slice) => ({
            ...slice,
            items: slice.items.filter((it) => !(it.kind === 'product' && it.lineId === lineId)),
        }));
    }, [updateActiveSlice]);
    const removeComboFromCart = useCallback((comboId) => {
        updateActiveSlice((slice) => ({
            ...slice,
            items: slice.items.filter((it) => !(it.kind === 'combo' && it.combo.id === comboId)),
        }));
    }, [updateActiveSlice]);
    const setCartQuantity = useCallback((lineId, quantity) => {
        updateActiveSlice((slice) => setProductQuantityInSlice(slice, lineId, quantity));
    }, [updateActiveSlice]);
    const setComboQuantity = useCallback((comboId, quantity) => {
        updateActiveSlice((slice) => setComboQuantityInSlice(slice, comboId, quantity));
    }, [updateActiveSlice]);
    const getCartQuantity = useCallback((productId) => activeSlice.items
        .filter((it) => it.kind === 'product' && it.product.id === productId)
        .reduce((sum, it) => sum + it.quantity, 0), [activeSlice.items]);
    const getComboQuantity = useCallback((comboId) => activeSlice.items.find((it) => it.kind === 'combo' && it.combo.id === comboId)?.quantity ?? 0, [activeSlice.items]);
    const clearCart = useCallback(() => {
        if (activeMenuId == null)
            return;
        setCartsByMenuId((prev) => ({ ...prev, [activeMenuId]: createEmptySlice() }));
        setUnavailableIds([]);
    }, [activeMenuId]);
    const markUnavailableProductIds = useCallback((ids) => {
        if (!Array.isArray(ids))
            return;
        setUnavailableIds((prev) => Array.from(new Set([...prev, ...ids.filter((n) => Number.isFinite(n))])));
    }, []);
    const clearUnavailableProductIds = useCallback(() => setUnavailableIds([]), []);
    const pickupRestaurantId = activeSlice.pickupRestaurantId;
    const isProductAvailable = useCallback((product) => {
        if (unavailableIds.includes(product.id))
            return false;
        if (pickupRestaurantId == null)
            return true;
        const disabled = Array.isArray(product.disabledRestaurantIds) ? product.disabledRestaurantIds : [];
        return !disabled.includes(pickupRestaurantId);
    }, [unavailableIds, pickupRestaurantId]);
    useEffect(() => {
        setUnavailableIds([]);
    }, [pickupRestaurantId]);
    const value = useMemo(() => ({
        cartItems: activeSlice.items,
        deliveryType: activeSlice.deliveryType,
        setDeliveryType,
        deliveryAddress: activeSlice.deliveryAddress,
        setDeliveryAddress,
        checkoutMenuId,
        setCheckoutMenuId,
        pickupRestaurantId: activeSlice.pickupRestaurantId,
        setPickupRestaurantId,
        addToCart,
        addComboToCart,
        removeProductFromCart,
        removeComboFromCart,
        setCartQuantity,
        setComboQuantity,
        getCartQuantity,
        getComboQuantity,
        clearCart,
        isProductAvailable,
        unavailableProductIds: unavailableIds,
        markUnavailableProductIds,
        clearUnavailableProductIds,
    }), [
        activeSlice,
        checkoutMenuId,
        setCheckoutMenuId,
        setDeliveryType,
        setDeliveryAddress,
        setPickupRestaurantId,
        addToCart,
        addComboToCart,
        removeProductFromCart,
        removeComboFromCart,
        setCartQuantity,
        setComboQuantity,
        getCartQuantity,
        getComboQuantity,
        clearCart,
        isProductAvailable,
        unavailableIds,
        markUnavailableProductIds,
        clearUnavailableProductIds,
    ]);
    return _jsx(CartContext.Provider, { value: value, children: children });
}
export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx)
        throw new Error('useCart must be used within CartProvider');
    return ctx;
}
